import React from 'react';
import { Link } from 'react-router-dom';
import { User, Sparkles, ArrowRight, Chrome } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface AuthPromptProps {
  onClose?: () => void;
  message?: string;
  title?: string;
}

const AuthPrompt: React.FC<AuthPromptProps> = ({ 
  onClose, 
  message = "Sign up to start creating amazing clips from your videos",
  title = "Ready to Get Started?"
}) => {
  const { loginWithGoogle } = useAuth();

  const handleGoogleLogin = async () => {
    try {
      await loginWithGoogle();
      onClose?.();
    } catch (error) {
      console.error('Google login error:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 relative animate-fade-up">
        {/* Close button */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        )}

        {/* Icon */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Sparkles className="h-8 w-8 text-white" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {title}
          </h2>
          
          <p className="text-gray-600">
            {message}
          </p>
        </div>

        {/* Benefits */}
        <div className="space-y-3 mb-8">
          <div className="flex items-center text-sm text-gray-700">
            <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
            AI-powered highlight detection
          </div>
          <div className="flex items-center text-sm text-gray-700">
            <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
            Unlimited clip generation
          </div>
          <div className="flex items-center text-sm text-gray-700">
            <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
            Export to all social platforms
          </div>
          <div className="flex items-center text-sm text-gray-700">
            <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
            Free forever plan available
          </div>
        </div>

        {/* Auth buttons */}
        <div className="space-y-4">
          {/* Google Sign Up */}
          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center space-x-3 py-3 px-4 border border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <Chrome className="h-5 w-5" />
            <span className="font-medium">Continue with Google</span>
          </button>

          {/* Email Sign Up */}
          <Link
            to="/signup"
            className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 flex items-center justify-center"
            onClick={onClose}
          >
            <User className="h-5 w-5 mr-2" />
            Sign Up with Email
            <ArrowRight className="h-5 w-5 ml-2" />
          </Link>

          {/* Login link */}
          <div className="text-center">
            <span className="text-sm text-gray-600">Already have an account? </span>
            <Link
              to="/login"
              className="text-sm text-red-600 hover:text-red-700 font-medium"
              onClick={onClose}
            >
              Sign In
            </Link>
          </div>
        </div>

        {/* Trust indicators */}
        <div className="mt-8 pt-6 border-t border-gray-100 text-center space-y-2">
          <p className="text-xs text-gray-500">
            Join 1,200+ creators • No credit card required
          </p>
                     <div className="bg-red-50 border border-red-200 rounded-lg p-2">
             <p className="text-xs text-red-700 font-medium">
               🎉 New users get 1 free video to test our AI + upgrade anytime
             </p>
           </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPrompt; 